import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ApiClient from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Trash2, Share2, FileText, ArrowLeft, Pencil, Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { FileUpload } from "@/components/file-upload";
import { useFileUpload, type UploadedFile } from "@/hooks/use-file-upload";
import { usePreLessonStore } from "@/hooks/use-pre-lesson-store";
import { FileUtils, PreLessonMaterialType, type PreLessonValues } from "@live-writing/models";

interface Material {
  id: string;
  title: string;
  link: string;
  type: PreLessonMaterialType;
  s3_key?: string;
  file_name?: string;
  content_type?: string;
}

const preLessonSchema = yup.object({
  title: yup.string().required("Title is required").min(3, "Title must be at least 3 characters"),
  subject: yup.string().required("Subject is required"),
  topic: yup.string().default(""),
  introduction: yup.string().default(""),
  instructionsForStudents: yup.string().default(""),
});

type PreLessonFormData = yup.InferType<typeof preLessonSchema>;

function CreatePreLesson() {
  const navigate = useNavigate();
  const { addLesson } = usePreLessonStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [tempCode] = useState(() => crypto.randomUUID().slice(0, 8).toUpperCase());

  const form = useForm<PreLessonFormData>({
    resolver: yupResolver(preLessonSchema),
    defaultValues: {
      title: "",
      subject: "",
      topic: "",
      introduction: "",
      instructionsForStudents: "",
    },
    mode: "onBlur",
  });

  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  const handleUploadComplete = useCallback((uploaded: UploadedFile) => {
    setMaterials((prev) => [
      ...prev,
      {
        id: uploaded.id,
        title: uploaded.name,
        link: uploaded.url,
        type: FileUtils.getTypeFromContentType(uploaded.contentType),
        s3_key: uploaded.key,
        file_name: uploaded.name,
        content_type: uploaded.contentType,
      },
    ]);
  }, []);

  const { isUploading, uploadFiles, error: uploadError } = useFileUpload({
    uploadEndpoint: "/pre-lesson/upload",
    getUploadParams: (file) => ({
      pre_lesson_code: tempCode,
      file_name: file.name,
      content_type: file.type,
    }),
    onUploadComplete: handleUploadComplete,
    onUploadError: (err) => setSubmitError(err.message),
  });

  const removeMaterial = (id: string) => {
    setMaterials((prev) => prev.filter((m) => m.id !== id));
  };

  const startEditingTitle = (material: Material) => {
    setEditingMaterialId(material.id);
    setEditingTitle(material.title);
  };

  const saveEditingTitle = () => {
    if (editingMaterialId && editingTitle.trim()) {
      setMaterials((prev) =>
        prev.map((m) => (m.id === editingMaterialId ? { ...m, title: editingTitle.trim() } : m))
      );
    }
    setEditingMaterialId(null);
    setEditingTitle("");
  };

  const cancelEditingTitle = () => {
    setEditingMaterialId(null);
    setEditingTitle("");
  };

  const handleFilesSelected = useCallback(
    (files: FileList) => {
      uploadFiles(files);
    },
    [uploadFiles]
  );

  const onSubmit = async (data: PreLessonFormData) => {
    setSubmitError(null);
    setIsSubmitting(true);

    const validMaterials = materials.map((m) => ({
      title: m.title,
      link: m.link,
      type: m.type,
      s3_key: m.s3_key,
      file_name: m.file_name,
      content_type: m.content_type,
    }));

    try {
      const responseData = await ApiClient.post<{ pre_lesson: PreLessonValues }>("/pre-lesson", {
        title: data.title,
        subject: data.subject,
        topic: data.topic || undefined,
        introduction: data.introduction || undefined,
        instructions_for_students: data.instructionsForStudents || undefined,
        materials: validMaterials.length > 0 ? validMaterials : undefined,
      });

      addLesson(responseData.pre_lesson);
      navigate("/");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-[26px] font-bold text-foreground">Create Pre Lesson</h1>
          </div>
          <p className="text-[14px] text-muted-foreground ml-8">
            <span className="font-semibold text-foreground">Checklist:</span> Upload lesson slides,
            worksheets, readings, and other materials your students need to prepare for class.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="border-gray-300 text-foreground font-medium gap-2 bg-white"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        </div>
      </div>

      {(submitError || uploadError) && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError || uploadError}
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex gap-6">
            <div className="flex-1 space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[13px] font-semibold">
                      Title <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Cell Structure and Function"
                        className="h-10 border-input bg-background text-[14px]"
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">
                        Subject <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Science"
                          className="h-10 border-input bg-background text-[14px]"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="topic"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">Topic</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Cell Biology"
                          className="h-10 border-input bg-background text-[14px]"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div>
                <p className="text-[13px] font-semibold text-foreground mb-2">Lesson Materials</p>
                <div className="space-y-3">
                  {materials.length > 0 ? (
                    <div className="space-y-2">
                      {materials.map((material) => (
                        <div
                          key={material.id}
                          className="flex items-center justify-between gap-3 bg-muted/50 border border-input rounded-md px-3 py-2"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-primary shrink-0" />
                            <div className="flex-1 min-w-0">
                              {editingMaterialId === material.id ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={editingTitle}
                                    onChange={(e) => setEditingTitle(e.target.value)}
                                    className="h-7 text-[13px] flex-1"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") saveEditingTitle();
                                      if (e.key === "Escape") cancelEditingTitle();
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={saveEditingTitle}
                                    className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={cancelEditingTitle}
                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <p className="text-[13px] font-medium text-foreground truncate">
                                    {material.title}
                                  </p>
                                  <p className="text-[12px] text-muted-foreground truncate">
                                    Uploaded file
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          {editingMaterialId !== material.id && (
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditingTitle(material)}
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeMaterial(material.id)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-input rounded-md p-4 text-center text-muted-foreground text-[13px]">
                      Upload documents using the panel on the right
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="introduction"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">Introduction</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Any specific notes about how to prepare your students?"
                          rows={4}
                          className="border-input bg-background text-[14px] resize-none"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="instructionsForStudents"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[13px] font-semibold">
                        Instructions for Students
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Assign specific notes or directions to students"
                          rows={4}
                          className="border-input bg-background text-[14px] resize-none"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="px-6 h-10 border-input text-foreground font-semibold hover:bg-gray-50 bg-white"
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || isUploading}
                  className="px-8 h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  {isSubmitting ? "SAVING..." : "SAVE"}
                </Button>
              </div>
            </div>

            <div className="w-[300px] shrink-0">
              <FileUpload onFilesSelected={handleFilesSelected} isUploading={isUploading} />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default CreatePreLesson;
